import copy from 'copy-to-clipboard';
import _ from 'lodash';

import { User as UserModel } from '../../../models';

import { redirectTo, ErrorsModal, Preloader } from '../../../utils';
import CoreLeagueSetupView from '../../../views/CoreLeagueSetup';
import Radio from "backbone.radio";

const celebrityPopupChannel = Radio.channel( 'celebrity_popup' );
const BYE_ROUNDS_IDS = JSON.parse(process.env.BYE_ROUNDS || '[]');

/**
 * @desc League Settings View Class
 */
export default class CoreLeagueSettingsView extends CoreLeagueSetupView {

	get template() {
		throw new Error("Need set template for child CoreRegenerationBarView class");
	}

	/**
	 * Set all UI for this view
	 * @returns object
	 */
	ui() {
		return {
			...super.ui(),
			update_league:       '.js-update-settings',
			leave_league:        '.js-leave-league',
			copy_league_code:    '.js-copy-league-code',
			copied_code:         '.js-copied-code',
			league_code:         '.js-league-code',
			remove_team:         '.js-remove-team',
			search_team:         '.js-search-team',
			load_more:           '.js-load-more',
			add_celebrity_teams: '.js-add-celebrity-teams',
			remove_league:       '.js-remove-league'
		};
	}

	/**
	 * Create events for this UI
	 * @returns object
	 */
	events() {
		return {
			...super.events(),
			'click @ui.submit_form':         'updateLeague',
			'submit @ui.league_form':        'updateLeague',
			'click @ui.leave_league':        'leaveLeague',
			'click @ui.copy_league_code':    'copyCode',
			'click @ui.remove_team':         'removeTeamFromLeague',
			'click @ui.search_team':         'searchTeam',
			'click @ui.load_more':           'loadMoreTeam',
			'click @ui.add_celebrity_teams': 'showCelebrityPopup',
			'click @ui.remove_league':       'removeLeague'
		};
	}

	/**
	 * Set model events
	 * @returns object
	 */
	modelEvents() {
		return {
			'league:update:errors':   'showError',
			'league:update:success':  'updateLeagueSuccess',
			'league:leave:errors':    'showError',
			'league:leave:success':   'leaveLeagueSuccess',
			'league:form:errors':     'checkFormErrors',
			'league:query:not:found': 'redirectToMyLeague',
			'league:remove:success':  'redirectToMyLeague',
			'league:remove:errors':   'showError'
		};
	}

	/**
	 * Initialize view data
	 */
	async initialize() {
		this.user            = UserModel;
		this.rounds          = this.getOption( 'rounds' );
		this.teams           = this.getOption( 'teams' );
		this.league_id       = this.getOption( 'league_id' );
		this.is_commissioner = false;
		this.offset          = 0;
		this.oldTeamList     = {};

		this.model.set({
			is_show_more: false,
			isByeRounds: BYE_ROUNDS_IDS.length > 0
		});

		Preloader.show();
		await this.fetchData();

		this.initListeners()
			.updateModel()
			.checkIsCommissioner()
			.updateView();

		Preloader.hide();
	}

	initListeners() {
		this.listenTo( this.teams, 'remove:team:error', this.removeTeamFromLeagueError );
		this.listenTo( this.teams, 'remove:team:success', this.updateLeagueTeams);
		this.listenTo( celebrityPopupChannel, 'added:celebrity:teams', this.addedCelebrityTeams );

		return this;
	}

	checkIsCommissioner() {
		let commissioner = this.model.get( 'commissioner' ),
			user_id      = this.user.get( 'id' );

		this.is_commissioner = commissioner === user_id;

		return this;
	}

	/**
	 * Get all base data for this class
	 * @returns {*|Promise.<Object>}
	 */
	fetchData() {
		return Promise.all( [
			this.rounds.fetch(),
			this.model.setShowUrl().fetch(
				{
					data: {
						id: this.league_id
					}
				}
			),
			this.teams.show_my({
				league_id: this.league_id
			})
		] );
	}

	/**
	 * Update base class model
	 * @returns {LeagueCreateView}
	 */
	updateModel() {
		if( !this.model
			.checkLeagueMember()
		) {
			// this.redirectToMyLeague();
		}

		let teams = this.teams.toJSON(),
			data       = {
				teams,
				is_show_more: _.size(teams) === this.teams.default_offset
			},
			status = this.model.get('status'),
			onlyFuture = status === 'scheduled';

		celebrityPopupChannel.trigger( 'update:popup' );

		this.model
			.updateFormData( this.rounds, onlyFuture )
			.set( data )
			.checkIsPossibleLeave(this.user.get( 'id' ))
			.setAvatar()
			.updateUserName();

		return this;
	}

	/**
	 * Update current league
	 * @returns {LeagueSettingsView}
	 */
	updateLeague() {
		if ( !this.model.isFormErrors() ) {
			Preloader.show();
			this.model.updateLeague();
		}

		return this;
	}

	/**
	 * Copy code to
	 */
	copyCode() {
		let code = this.getUI( 'league_code' ).text();
		copy( code );
		this.getUI( 'copied_code' ).removeClass( 'hidden' );
	}

	/**
	 * Exit current league
	 */
	leaveLeague() {
		let confirm_remove_league = confirm('Are you sure you want to leave this league?');

		if( confirm_remove_league  ) {
			Preloader.show();
			this.model.leave();
		}
	}

	get remove_text() {
		return "Are you sure you want to delete this league ?";
	}

	/**
	 * Remove current league
	 */
	removeLeague() {
		let league_status = this.model.get('status');

		if( league_status === "scheduled" ) {
			let confirm_remove_league = confirm(this.remove_text);

			if( confirm_remove_league  ) {
				Preloader.show();
				this.model.remove();
			}
		}

		return this;
	}

	/**
	 * Model response Handling
	 */
	updateLeagueSuccess() {
		Preloader.hide();
		this.getUI('submit_form').text('Updated').addClass('tertiary-btn');
	}

	/**
	 * Model error Handling
	 * @param {object} errors
	 */
	showError( errors ) {
		Preloader.hide();
		ErrorsModal.show( errors );
	}

	removeTeamFromLeague( element ) {
		let status = this.model.get( 'status' );

		if( status === 'scheduled' ) {
			let team_id = $(element.currentTarget).data('id');
			let confirm_remove = confirm("Are you sure you want to delete this team ?");

			if( confirm_remove ) {
				$(element.currentTarget).closest('.table-row-item').hide();

				let data = {
					[this.removeTeamDataTeamKey]:team_id,
					[this.removeTeamDataLeagueKey]: this.model.get( 'id' )
				};

				this.teams.removeTeam( data );
			}

		}
	}

	get removeTeamDataTeamKey() {
		return 'team_id';
	}

	get removeTeamDataLeagueKey() {
		return 'league_id';
	}

	showCelebrityPopup() {
		let status = this.model.get( 'status' );
		if( status === 'scheduled' ) {
			celebrityPopupChannel.trigger( 'open:celebrity:popup' );
		}
	}

	searchTeam() {
		let status = this.model.get( 'status' );
		if( status === 'scheduled' ) {
			console.log('searchTeam');
		}
	}

	loadMoreTeam() {
		this.offset += this.teams.default_offset;
		this.oldTeamList = this.teams.toJSON();

		this.getMoreTeams({
			offset:    this.offset,
			league_id: this.league_id
		});
	}

	getMoreTeams(data = {}) {
		this.teams.show_my(data).then(() => {
			let is_show_more = this.teams.length === this.teams.default_offset;

			this.teams.reset(
				this.oldTeamList.concat( this.teams.toJSON() ),
				{
					silent: true
				}
			);

			this.model.set( {
				teams:  this.teams.toJSON(),
				is_show_more
			});

			celebrityPopupChannel.trigger( 'update:popup' );
			this.updateView();
		});
	}

	redirectToMyLeague() {
		redirectTo( '/fantasy/leagues' );
	}

	/**
	 * Model response Handling
	 */
	leaveLeagueSuccess() {
		Preloader.hide();
		this.redirectToMyLeague();
	}

	removeTeamFromLeagueError( error ) {
		ErrorsModal( error );
	}

	addedCelebrityTeams() {
		this.oldTeamList = {};
		this.updateLeagueTeams();

		return this;
	}

	get updateTeamsData() {
		return {
			league_id: this.league_id
		}
	}

	updateLeagueTeams() {
		this.teams.show_my(this.updateTeamsData).then(() => {
			this.model.set( { teams:  this.teams.toJSON() });
			celebrityPopupChannel.trigger( 'update:popup' );
			this.updateView();
		});
	}
}